import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';
const inputCls = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const selectCls = inputCls;
const cardCls = "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700";
const EMPTY_FORM = { name:'', parent_id:'', latitude:'', longitude:'', zoom:11 };

export default function Regions() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const authHeader = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

  const fetchRegions = useCallback(async () => {
    try { const res = await axios.get(`${BACKEND_URL}/api/regions`, { headers: authHeader }); setRegions(res.data||[]); }
    catch { toast.error('Gabim gjate ngarkimit'); }
  }, []);

  useEffect(() => { const init = async () => { setLoading(true); await fetchRegions(); setLoading(false); }; init(); }, []);

  const openAdd = () => { setEditingRegion(null); setForm(EMPTY_FORM); setShowForm(true); setTimeout(()=>document.getElementById('region-name-input')?.focus(),50); };
  const openEdit = (r) => {
    setEditingRegion(r);
    setForm({ name:r.name||'', parent_id:r.parent_id?String(r.parent_id):'', latitude:r.latitude??'', longitude:r.longitude??'', zoom:r.zoom??11 });
    setShowForm(true); setExpandedId(null); setTimeout(()=>document.getElementById('region-name-input')?.focus(),50);
  };
  const cancelForm = () => { setShowForm(false); setEditingRegion(null); setForm(EMPTY_FORM); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Emri eshte i detyrueshem'); return; }
    setSaving(true);
    try {
      const payload = { ...form, zoom:Number(form.zoom)||11, parent_id:form.parent_id||null, latitude:form.latitude!==''?Number(form.latitude):null, longitude:form.longitude!==''?Number(form.longitude):null };
      if (editingRegion) { await axios.put(`${BACKEND_URL}/api/regions/${editingRegion.id}`, payload, { headers: authHeader }); toast.success('Rajoni u perditesua'); }
      else { await axios.post(`${BACKEND_URL}/api/regions`, payload, { headers: authHeader }); toast.success('Rajoni u krijua'); }
      cancelForm(); await fetchRegions();
    } catch (err) { toast.error(err.response?.data?.message||'Gabim'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`A jeni te sigurt qe doni te fshini "${r.name}"?`)) return;
    setDeletingId(r.id);
    try { await axios.delete(`${BACKEND_URL}/api/regions/${r.id}`, { headers: authHeader }); toast.success('Rajoni u fshi'); setExpandedId(null); await fetchRegions(); }
    catch { toast.error('Gabim'); } finally { setDeletingId(null); }
  };

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));
  const parentName = (id) => regions.find(r=>String(r.id)===String(id))?.name || '–';

  return (
    <Layout>
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Menaxhimi i Rajoneve</h1>
          {!showForm && <button onClick={openAdd} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition">+ Shto Rajon</button>}
        </div>

        {/* ── Forma ── */}
        {showForm && (
          <div className={`${cardCls} p-5`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">{editingRegion ? 'Ndrysho Rajonin' : 'Shto Rajon te Ri'}</h2>
              <button type="button" onClick={cancelForm} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none">&times;</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Emri *</label>
                  <input id="region-name-input" type="text" value={form.name} onChange={f('name')} className={inputCls} required placeholder="p.sh. Gjakova" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Rajoni prind (opsional)</label>
                  <select value={form.parent_id} onChange={f('parent_id')} className={selectCls}>
                    <option value="">-- Asnje --</option>
                    {regions.filter(r=>!editingRegion||r.id!==editingRegion.id).map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Zoom (1-18)</label>
                  <input type="number" min="1" max="18" value={form.zoom} onChange={f('zoom')} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Gjeresia (Lat)</label>
                  <input type="number" step="any" value={form.latitude} onChange={f('latitude')} className={inputCls} placeholder="42.6026" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Gjatesia (Lng)</label>
                  <input type="number" step="any" value={form.longitude} onChange={f('longitude')} className={inputCls} placeholder="20.9030" />
                </div>
              </div>
              <div className="flex gap-3 mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition">
                  {saving ? 'Duke ruajtur...' : (editingRegion ? 'Perditeso' : 'Shto Rajon')}
                </button>
                <button type="button" onClick={cancelForm} className="px-5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition">Anulo</button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400 text-sm">Duke ngarkuar...</div>
        ) : regions.length === 0 ? (
          <div className={`${cardCls} p-8 text-center text-gray-500 dark:text-gray-400 text-sm`}>
            Asnje rajon. {!showForm && <button onClick={openAdd} className="text-blue-600 hover:underline">Shto rajonin e pare</button>}
          </div>
        ) : (
          <>
            {/* ══ MOBILE: karta me expand ══ */}
            <div className="md:hidden space-y-2">
              {regions.map(r => {
                const isOpen = expandedId === r.id;
                const parent = parentName(r.parent_id);
                const hasCoords = r.latitude != null && r.longitude != null;
                return (
                  <div key={r.id} className={`${cardCls} overflow-hidden`}>
                    {/* Rreshti kryesor */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <div className="w-9 h-9 rounded-xl bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{r.name}</p>
                        {parent !== '–' && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            <span className="text-gray-400">↳</span> {parent}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded font-mono">z{r.zoom??11}</span>
                        <button
                          type="button"
                          onClick={() => setExpandedId(isOpen ? null : r.id)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition ${
                            isOpen ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="5" cy="12" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="19" cy="12" r="1.5"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Expand */}
                    {isOpen && (
                      <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 space-y-2 bg-gray-50 dark:bg-gray-800/60">
                        {hasCoords ? (
                          <>
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Gjerësia (Lat)</span>
                              <span className="text-xs font-mono font-medium text-gray-700 dark:text-gray-300">{r.latitude}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Gjatësia (Lng)</span>
                              <span className="text-xs font-mono font-medium text-gray-700 dark:text-gray-300">{r.longitude}</span>
                            </div>
                          </>
                        ) : (
                          <p className="text-xs text-gray-400 italic">Pa koordinata</p>
                        )}
                        <div className="flex justify-between">
                          <span className="text-xs text-gray-400">Zoom</span>
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{r.zoom??11}</span>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          <button onClick={() => openEdit(r)} className="flex-1 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition">
                            ✏️ Ndrysho
                          </button>
                          <button onClick={() => handleDelete(r)} disabled={deletingId===r.id} className="flex-1 py-2 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-400 text-sm font-medium rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 transition disabled:opacity-50">
                            {deletingId===r.id ? '...' : '🗑️ Fshi'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ══ DESKTOP: tabela ══ */}
            <div className={`${cardCls} overflow-hidden hidden md:block`}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>{['Emri','Rajoni prind','Gjeresia','Gjatesia','Zoom','Veprimet'].map(h=>(
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {regions.map(r => (
                      <tr key={r.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition ${editingRegion?.id===r.id?'bg-blue-50 dark:bg-blue-900/10':''}`}>
                        <td className="px-3 py-2.5 font-medium text-gray-900 dark:text-white">{r.name}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{parentName(r.parent_id)}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">{r.latitude!=null?r.latitude:'–'}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">{r.longitude!=null?r.longitude:'–'}</td>
                        <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400">{r.zoom??'–'}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex gap-2">
                            <button onClick={()=>openEdit(r)} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg transition">Ndrysho</button>
                            <button onClick={()=>handleDelete(r)} disabled={deletingId===r.id} className="px-3 py-1 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-xs font-medium rounded-lg transition disabled:opacity-50">
                              {deletingId===r.id?'...':'Fshi'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
